import { useState } from "react";
import { useSEO } from "@/hooks/use-seo";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CheckCircle, ChevronDown, ChevronUp, Shield, AlertTriangle, BookOpen, ArrowRight, Calendar } from "lucide-react";
import mykhailoPhoto from "@assets/image_1773345523383.png";
import rebecaPhoto from "@assets/image_1773345527765.png";
import { Link } from "wouter";
import scanImg from "@assets/image_1773172306379.png";
import omahaImg from "@assets/image_1773172311312.png";

const HANDS = [
  { rank: 1, name: "Královská postupka", en: "Royal flush",    desc: "A-K-Q-J-10 v jedné barvě (flush)",                   example: "A♠ K♠ Q♠ J♠ 10♠" },
  { rank: 2, name: "Postupka v barvě",   en: "Straight flush", desc: "Pět po sobě jdoucích karet v jedné barvě (flush a straight)", example: "9♣ 8♣ 7♣ 6♣ 5♣" },
  { rank: 3, name: "Čtveřice",           en: "Four of a kind", desc: "Čtyři stejně hodnotné karty",                        example: "A♣ A♦ A♥ A♠ K♦" },
  { rank: 4, name: "Full house",         en: "Full house",     desc: "Trojice + pár",                                       example: "K♦ K♥ K♠ 4♣ 4♥" },
  { rank: 5, name: "Barva",              en: "Flush",          desc: "Pět karet jedné barvy",                              example: "5♥ 8♥ J♥ A♥ 2♥" },
  { rank: 6, name: "Postupka",           en: "Straight",       desc: "Pět po sobě jdoucích karet, barvy libovolně",        example: "4♠ 5♥ 6♦ 7♣ 8♠" },
  { rank: 7, name: "Trojice",            en: "Three of a kind",desc: "Tři karty stejné hodnoty",                           example: "7♠ 7♦ 7♥ 2♣ 9♦" },
  { rank: 8, name: "Dvě dvojice",        en: "Two pair",       desc: "Dva páry + pátá karta",                              example: "Q♣ Q♥ 3♠ 3♦ K♠" },
  { rank: 9, name: "Jeden pár",          en: "One pair",       desc: "Pár + tři jiné karty",                               example: "10♣ 10♥ 5♦ 8♦ K♣" },
  { rank: 10, name: "Vysoká karta",      en: "High card",      desc: "Nic výše, rozhoduje nejvyšší karta",                 example: "A♥ 9♦ 7♠ 4♣ 2♠" },
];

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#eef1f7] last:border-0">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-4 text-left gap-4">
        <span className="font-semibold text-gray-900 text-sm">{q}</span>
        {open ? <ChevronUp className="w-4 h-4 text-[#003da5] flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
      </button>
      {open && <p className="text-gray-600 text-sm leading-relaxed pb-4">{a}</p>}
    </div>
  );
}

export default function PokerKombinacePage() {
  useSEO({ title: "Pokrové Kombinace 2026 – Přehled rukou a jejich hodnota | HrajBonus", description: "Kompletní přehled všech pokrových kombinací od Royal Flush po pár. Naučte se rozpoznat vítěznou ruku rychle a spolehlivě. S příklady. 18+",
  url: "https://hrajbonus.com/poker/kombinace", });

  return (
    <div className="min-h-screen bg-[#f5f7fb]" style={{ fontFamily: "Poppins, sans-serif" }}>
      <Header />

      {/* Hero */}
      <section className="relative pt-16 pb-0 overflow-hidden">
        <div className="absolute inset-0 z-0" style={{ background: "linear-gradient(135deg, #001a4d 0%, #002d7c 50%, #003da5 100%)" }} />
        <div className="absolute inset-0 z-0 opacity-5" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "40px 40px" }} />
        <div className="relative z-10 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16 py-14 sm:py-16">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div className="flex items-center gap-2 text-white/60 text-sm font-semibold">
              <Link href="/poker" className="hover:text-white transition-colors">Poker</Link>
              <span>/</span>
              <span className="text-white/90">Kombinace</span>
            </div>
            <div className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 rounded-full px-3 py-1.5 text-white/70 text-xs font-semibold" data-testid="badge-aktualizovano-kombinace">
              <Calendar className="w-3 h-3" /> Aktualizováno: 12. března 2026
            </div>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-white leading-tight mb-5">
            Základy pokerové kombinace
          </h1>
          <p className="text-white/75 text-lg leading-relaxed mb-5">
            Pokerové kombinace jsou jazyk, kterým se u stolu domlouvá síla v každé hře, a royal flush je nejsilnější kombinací ze všech. Když je čtete rychle a bez chyb, ušetříte si drahé omyly: zbytečné cally do hotové barvy nebo flush, přeceněný pár na nebezpečném boardu nebo naopak složení ruky, která měla vyhrávat.
          </p>
          <p className="text-white/60 text-base leading-relaxed mb-6">
            A dobrá zpráva je, že se to dá naučit překvapivě svižně. Stačí mít jasno v pořadí kombinací, rozumět kickerům a umět skenovat společné karty tak, aby vám žádná výherní kombinace neutekla. Mezi tyto kombinace patří flush, straight, straight flush a royal flush.
          </p>
          <div className="flex items-center gap-2 bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 w-fit text-white/70 text-xs font-semibold">
            <Shield className="w-3.5 h-3.5 text-white/50" /> Pouze pro hráče 18+. Hrajte zodpovědně.
          </div>
        </div>
        <div className="relative z-10">
          <svg viewBox="0 0 1440 44" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full block" preserveAspectRatio="none" style={{ height: "44px" }}>
            <rect x="0" y="40" width="1440" height="4" fill="#f5f7fb" />
            <path d="M0 44L1440 44L1440 0C1200 35 720 40 0 0L0 44Z" fill="#f5f7fb" />
          </svg>
        </div>
      </section>

      {/* Poker cross-navigation */}
      <nav className="bg-[#f5f7fb] border-b border-[#dde4ee] sticky top-16 z-20" data-testid="poker-nav">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16">
          <div className="flex items-center gap-1 overflow-x-auto py-3 -mx-1">
            {[
              { href: "/poker", label: "Poker přehled", active: false },
              { href: "/poker/kombinace", label: "Kombinace", active: true },
              { href: "/poker/strategie", label: "Strategie", active: false },
              { href: "/poker/jak-hrat", label: "Jak hrát", active: false },
            ].map(({ href, label, active }) => (
              <Link key={href} href={href}>
                <div
                  className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                    active
                      ? "bg-[#003da5] text-white"
                      : "text-gray-600 hover:bg-white hover:text-[#003da5]"
                  }`}
                  data-testid={`poker-nav-${href.replace(/\//g, "-")}`}
                >
                  {label}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </nav>

      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16 py-10 space-y-8">

        {/* Hand rankings visual cards */}
        <section>
          <div className="bg-white rounded-2xl border border-[#dde4ee] shadow-sm overflow-hidden">
            <div className="px-6 py-5 border-b border-[#eef1f7]">
              <h2 className="text-2xl font-black text-gray-900 mb-1">Pořadí kombinací: co poráží co</h2>
              <p className="text-gray-600 text-sm leading-relaxed">
                V klasickém pokeru (Texas Hold'em, Omaha a většina dalších variant) se porovnává vždy nejlepší pětikaretní kombinace, kterou dokážete složit z dostupných karet. Dealer při rozdávání dohlíží na to, aby bylo vše spravedlivé a v souladu s poker pravidly.
              </p>
              <p className="text-gray-500 text-sm leading-relaxed mt-2">
                Níže najdete přehled od nejsilnější kombinace po nejslabší — základní referenci, kterou je dobré mít v hlavě ještě předtím, než se pustíte do složitých situací u stolu.
              </p>
            </div>
            <div className="p-6">
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                {HANDS.map((h) => {
                  const cards = h.example.split(" ");
                  return (
                    <div
                      key={h.rank}
                      className="relative bg-[#f3f6fb] border border-[#dde4ee] rounded-xl p-4 hover:shadow-md transition-shadow"
                      data-testid={`hand-card-${h.rank}`}
                    >
                      <div className="absolute top-3 right-3 w-7 h-7 rounded-full bg-[#003da5] text-white font-black text-xs flex items-center justify-center">
                        {h.rank}
                      </div>
                      <div className="flex flex-wrap gap-1 mb-3 min-h-[40px]">
                        {cards.map((card, ci) => (
                          <span
                            key={ci}
                            className={`inline-flex items-center justify-center w-9 h-12 bg-white border border-[#dde4ee] rounded-lg font-mono font-bold text-sm shadow-sm ${
                              card.includes("♥") || card.includes("♦") ? "text-red-500" : "text-gray-800"
                            }`}
                          >
                            {card}
                          </span>
                        ))}
                      </div>
                      <div className="font-bold text-gray-900 text-sm">{h.name}</div>
                      <div className="text-gray-400 text-xs italic mb-1">{h.en}</div>
                      <p className="text-gray-600 text-xs leading-relaxed">{h.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="px-6 py-4 bg-[#f0f5ff] border-t border-[#003da5]/10">
              <p className="text-[#003da5] text-sm font-semibold">
                Jedna věta, která ušetří spoustu času: barva (flush) poráží postupku (straight), ale prohrává s full housem.
              </p>
            </div>
          </div>
        </section>

        {/* How to recognise combinations + image */}
        <section>
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-4">Jak rychle poznat kombinace během hry (bez zmatků)</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              U začátečníků v pokeru se chyby typicky dějí ne proto, že by pořadí neznali, ale protože ve stresu nevidí board. Pomáhá jednoduchý návyk: po flopu, turnu i riveru udělat krátký mentální sken a vždy ve stejném pořadí.
            </p>
            <p className="text-gray-600 leading-relaxed mb-5">
              Nejdřív hledejte, co může existovat už na stole (a co může mít soupeř), teprve potom vyhodnocujte svou konkrétní sílu. To je bezpečnější, protože vás to chrání před přeceněním jedné pěkné karty v ruce. V každé hře se totiž může stát, že i malá sázka bude mít zásadní dopad na průběh partie.
            </p>

            <div className="bg-[#f3f6fb] rounded-xl p-5 mb-6">
              <div className="font-bold text-gray-900 text-sm mb-3">Po každé ulici si projděte rychlou kontrolu:</div>
              <div className="grid sm:grid-cols-2 gap-2">
                {[
                  "Barvy",
                  "Postupkové mezery (straight)",
                  "Páry a trojice na boardu (včetně full house)",
                  "Nejvyšší karta na stole (a jak se mění)",
                ].map(item => (
                  <div key={item} className="flex items-center gap-2 text-gray-700 text-sm">
                    <CheckCircle className="w-4 h-4 text-[#003da5] flex-shrink-0" />
                    {item}
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-xs mt-3 leading-relaxed">
                Tento mikrorituál trvá pár vteřin, ale výrazně sníží počet omylů typu myslel jsem, že mám postupku, nebo nevšiml jsem si třetího srdce na turnu.
              </p>
            </div>

            <div className="rounded-2xl overflow-hidden border border-[#dde4ee]">
              <img src={scanImg} alt="Jak rychle poznat kombinace během hry" className="w-full h-auto block" />
            </div>
          </div>
        </section>

        {/* Kickers, ties, details */}
        <section>
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-4">Remízy, kickery a detaily, které rozhodují o penězích</h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              Poker se často nerozhoduje tím, že by jeden měl barvu a druhý pár. Častější jsou situace, kdy oba hráči drží podobnou kombinaci a rozhodují drobnosti. Může jít o správné sázky, kdy přesná výše sázky ovlivní, kdo nakonec vyhraje.
            </p>

            <div className="space-y-5">
              <div className="bg-[#f3f6fb] rounded-xl p-5">
                <h3 className="font-bold text-gray-900 text-base mb-3">Kicker: váš tichý spolurozhodčí</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-3">
                  Kicker je nejvyšší zbylá karta, která doplňuje kombinaci. Má smysl hlavně u páru, dvou párů, high card nebo výherních kombinací, kdy třeba oba hráči mají pár desítek.
                </p>
                <div className="bg-white border border-[#dde4ee] rounded-lg p-3 text-sm">
                  <span className="font-bold text-gray-900">Příklad: </span>
                  <span className="text-gray-600">Oba hráči mají pár desítek, ale jeden má A kicker, zatímco druhý má K kicker. Vyhrává eso, protože kombinace se porovnává od nejvyšší relevantní karty.</span>
                </div>
              </div>

              <div className="bg-[#f3f6fb] rounded-xl p-5">
                <h3 className="font-bold text-gray-900 text-base mb-3">Postupka s esem: pozor na A-2-3-4-5</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-2">
                  Eso může být v postupce nahoře (10-J-Q-K-A) nebo dole (A-2-3-4-5). Nemůže přeskakovat jako K-A-2-3-4.
                </p>
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <span className="text-amber-700 text-sm">Jednoduché pravidlo: u nejnižší postupky se eso počítá jako jednička.</span>
                </div>
              </div>

              <div className="bg-[#f3f6fb] rounded-xl p-5">
                <h3 className="font-bold text-gray-900 text-base mb-3">Když je na stole hotová kombinace</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-3">
                  V Texas Hold'emu se běžně stane, že na boardu leží třeba postupka (straight), barva (flush), royal flush nebo dokonce straight flush, takže ji má každý. Pak rozhodují detaily:
                </p>
                <ul className="space-y-2">
                  {[
                    "U barvy nejvyšší karta v barvě (pokud si ji tvoříte z vlastních karet)",
                    "U postupky nejvyšší karta postupky",
                    "U full housu pořadí trojice a páru — A-A-A-K-K poráží K-K-K-A-A",
                  ].map(item => (
                    <li key={item} className="flex items-start gap-2 text-gray-600 text-sm">
                      <span className="text-[#003da5] font-bold mt-0.5 flex-shrink-0">→</span> {item}
                    </li>
                  ))}
                </ul>
                <p className="text-gray-500 text-xs mt-3">Někdy je to čistá remíza a pot se dělí — standardní výsledek v pokeru.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Texas Hold'em vs Omaha + image */}
        <section>
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-4">Texas Hold'em vs. Omaha: stejná tabulka, jiné nástrahy</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              Pořadí poker kombinací je stejné, jen se jinak skládají dostupné karty. Přesto základní sázkové principy, které určují bluffování a strategii, zůstávají podobné.
            </p>

            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              <div className="bg-[#f0f5ff] border border-[#003da5]/15 rounded-xl p-4">
                <div className="font-bold text-[#003da5] text-sm mb-2">Texas Hold'em</div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Kombinujete libovolně své 2 karty a 5 společných. Při každé hře je tedy třeba si dobře rozmyslet, kterou kombinaci si chcete vytvořit.
                </p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="font-bold text-amber-800 text-sm mb-2">Omaha</div>
                <p className="text-gray-700 text-sm leading-relaxed">
                  Máte 4 karty v ruce, ale musíte použít přesně 2 z nich a přesně 3 ze společných. V Omaze lidé často chybují u barev: mají v ruce tři srdce, na stole jsou dvě srdce a automaticky si vidí flush — jenže flush nevzniká, protože ze své ruky smíte vzít jen dvě karty.
                </p>
              </div>
            </div>

            <div className="bg-[#f3f6fb] rounded-xl p-4 mb-6">
              <div className="font-bold text-gray-900 text-sm mb-2">Jedna krátká pomůcka pro Omahu:</div>
              <p className="text-gray-600 text-sm leading-relaxed">
                Po flopu vytipujte dvě konkrétní karty z ruky, které chcete používat. Pokud to nejde, kombinace neexistuje a sázka, nebo případné blafování, by mohly vést k drahým chybám.
              </p>
            </div>

            <div className="rounded-2xl overflow-hidden border border-[#dde4ee]">
              <img src={omahaImg} alt="Texas Hold'em vs Omaha: stejná tabulka, jiné nástrahy" className="w-full h-auto block" />
            </div>
          </div>
        </section>

        {/* Street by street */}
        <section>
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-4">Kombinace v praxi podle ulic: co sledovat preflop, flop, turn, river</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              Kombinace nejsou jen slovíčka z testu. Ovlivňují, jak často sázet, kdy přitlačit a kdy naopak brzdit, protože board je mokrý a nabízí soupeřům mnoho možností. V pokeru každá sázka musí být promyšlená a zároveň je důležité rozpoznat, kdy hraje správná hra, což je základem úspěšné poker strategie.
            </p>
            <p className="text-gray-600 leading-relaxed mb-5">
              Na začátku pomůže uvažovat jednoduše: nejdřív síla kombinace, pak riziko a potom pozice u stolu.
            </p>

            <div className="bg-[#f3f6fb] rounded-xl p-5 mb-5">
              <div className="font-bold text-gray-900 text-sm mb-3">Po odhalení flopu a dalších karet si pokládejte otázky:</div>
              <div className="space-y-2">
                {[
                  "Co už porážím teď?",
                  "Co mě poráží teď?",
                  "Co se může dorazit na turnu nebo riveru?",
                  "Až potom řešte velikost sázky.",
                ].map((q, i) => (
                  <div key={i} className="flex items-start gap-2.5 text-gray-700 text-sm">
                    <span className="text-[#003da5] font-black mt-0.5 flex-shrink-0">{i + 1}.</span> {q}
                  </div>
                ))}
              </div>
            </div>

            <div className="font-bold text-gray-900 text-sm mb-3">Praktický rámec rozhodování:</div>
            <div className="grid sm:grid-cols-2 gap-3 mb-5">
              {[
                { title: "Silná hotová kombinace", desc: "Set, dvě dvojice, hotová barva či straight flush na bezpečnějším boardu — často chcete sázet pro hodnotu, aby vám soupeř zaplatil.", color: "bg-green-50 border-green-200", titleColor: "text-green-800" },
                { title: "Zranitelná síla", desc: "Top pár na boardu s mnoha draw možnostmi — potřebujete chránit svou kombinaci agresivní sázkou.", color: "bg-yellow-50 border-yellow-200", titleColor: "text-yellow-800" },
                { title: "Rozumný tlak", desc: "Semi-bluff s flush draw nebo otevřenou postupkou — hrajte tak, aby šlo vyhrát hned, nebo se levně podívat na další kartu.", color: "bg-blue-50 border-blue-200", titleColor: "text-blue-800" },
                { title: "Brzda včas", desc: "Když se na turnu dokončí zjevná draw a vy máte jen pár — dává smysl opatrnost.", color: "bg-red-50 border-red-200", titleColor: "text-red-800" },
              ].map(item => (
                <div key={item.title} className={`${item.color} border rounded-xl p-4`}>
                  <div className={`font-bold text-sm mb-1.5 ${item.titleColor}`}>{item.title}</div>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
            <p className="text-gray-500 text-sm leading-relaxed">
              I při každé sázce na hru je nutné myslet také na možnost blafování, kdy soupeř může využít vašeho přeceňování nebo podcenění situace.
            </p>
          </div>
        </section>

        {/* Board scanning */}
        <section>
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-4">Jak skenovat board bezpečněji než soupeř</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              Zkušení hráči v pokeru často nevynikají tím, že by znali víc kombinací, nýbrž tím, že si dokáží jednu věc všimnout dříve než ostatní. Pomáhá sledovat dvě věci najednou: co skládáte vy a co se nabízí celé tabuli.
            </p>
            <div className="bg-[#f3f6fb] rounded-xl p-5 mb-5">
              <div className="font-bold text-gray-900 text-sm mb-3">Krátký checklist pro každou ulici:</div>
              <div className="space-y-2">
                {[
                  "Spočítat, kolik karet jedné barvy a flush draw je na stole.",
                  "Zkontrolovat, zda jsou hodnoty blízko sebe — potenciálně straight, straight flush, nebo dokonce royal flush.",
                  "Všimnout si páru na boardu (možný full house) nebo jiných shod.",
                  "Vyhodnotit nejvyšší kartu a jak trefuje typické startovní kombinace soupeřů.",
                ].map(item => (
                  <div key={item} className="flex items-start gap-2 text-gray-700 text-sm">
                    <CheckCircle className="w-4 h-4 text-[#003da5] mt-0.5 flex-shrink-0" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-amber-700 text-sm leading-relaxed">
                Když na turnu přijde třetí karta do barvy nebo se board spáruje, mnoho hráčů to zaregistruje až příliš pozdě. A právě v té chvíli může dojít k drahým sázkám nebo k neúspěšnému blafování.
              </p>
            </div>
          </div>
        </section>

        {/* Memorisation tips */}
        <section>
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-4">Jak si kombinace zapamatovat bez biflování</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              Zapamatovat si pořadí je rychlé, ale těžší je rozpoznávat kombinace automaticky, když běží čas a vy zároveň řešíte sizing sázky, pozici a styl soupeřova blafování. Funguje krátký, opakovaný trénink — ideálně po minutách, ne až po hodinách.
            </p>
            <div className="grid sm:grid-cols-3 gap-4 mb-5">
              {[
                { title: "Bleskový kvíz", desc: "Otevřete generátor karet nebo si vezměte balíček a náhodně si vyložte 5 až 7 karet. V řádu 3 vteřin pojmenujte nejlepší kombinaci.", icon: "⚡" },
                { title: "Kartičky", desc: "Na jedné straně obrázek kombinace, na druhé název. Spaced repetition: dnes, zítra, za tři dny, za týden.", icon: "🃏" },
                { title: "Online tahák", desc: "Při online pokeru mimo hru může pomoci přehledný hand rank tahák, který shrnuje poker pravidla. Při hře už spoléhejte na vlastní rutinu.", icon: "📋" },
              ].map(item => (
                <div key={item.title} className="bg-[#f3f6fb] rounded-xl p-4">
                  <div className="text-2xl mb-2">{item.icon}</div>
                  <div className="font-bold text-gray-900 text-sm mb-2">{item.title}</div>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>

            <div className="bg-[#003da5] rounded-xl p-5 text-white">
              <div className="font-bold text-base mb-2">Mini trénink na 2 minuty (kdykoliv)</div>
              <p className="text-white/80 text-sm leading-relaxed">
                Vezměte si svůj oblíbený balíček karet nebo využijte generátor karet, a pětkrát po sobě si zahrajte mini hru: otočte 7 karet, najděte nejlepší pětikaretní výherní kombinaci a řekněte nahlas, co to je a jaký kicker máte. Dvě minuty denně mohou výrazně zlepšit vaše šance při sázkách a celkovou strategii.
              </p>
            </div>
          </div>
        </section>

        {/* Safety */}
        <section>
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-4">Bezpečnost, férovost a zodpovědné hraní</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              U online pokeru se dá řešit i mnoho věcí mimo samotné výherní kombinace. Důležité je mít správně nastavené limity, volit důvěryhodná kasina a hru vždy hrát zodpovědně. Před každou sázkou se ujistěte, že jste si ověřili licenci a reputaci herny, kde poker hrajete.
            </p>
            <p className="text-gray-600 leading-relaxed mb-5">
              Jedna věta, kterou je dobré mít po ruce: Když se přistihnete, že musíte vyhrát zpět, je správný čas dát pauzu. I při každé sázce a každé hře je třeba myslet na to, že poker je především hra, kde si navzájem soupeři uplatňují i blafování.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                "Nastavte si limity vkladů a sázek předem",
                "Vybírejte kasina s platnou licencí a ověřenou reputací",
                "Sledujte čas strávený hraním a dělejte přestávky",
              ].map(item => (
                <div key={item} className="flex items-start gap-2.5 text-gray-700 text-sm">
                  <Shield className="w-4 h-4 text-[#003da5] mt-0.5 flex-shrink-0" />
                  {item}
                </div>
              ))}
              <div className="flex items-start gap-2.5 text-gray-700 text-sm">
                <Shield className="w-4 h-4 text-[#003da5] mt-0.5 flex-shrink-0" />
                Pokud ztrácíte kontrolu, využijte sebeomezení v kasinu nebo kontaktujte{" "}
                <a href="https://www.gamcare.org.uk/" target="_blank" rel="noopener noreferrer" className="text-[#003da5] hover:underline font-semibold" data-testid="link-gamcare-poker-kombinace">GamCare</a>
              </div>
            </div>
          </div>
        </section>

        {/* Author */}
        <section>
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className="w-14 h-14 rounded-full overflow-hidden ring-2 ring-[#dde4ee]">
                  <img src={mykhailoPhoto} alt="Mykhailo Romaenko" className="w-full h-full object-cover" />
                </div>
              </div>
              <div className="flex-1">
                <div className="font-bold text-gray-900 text-base">Mykhailo Romaenko</div>
                <div className="text-[#003da5] text-xs font-semibold mb-3">Senior Content Writer</div>
                <p className="text-gray-600 text-sm leading-relaxed mb-3">
                  Mykhailo je zkušený autor zaměřený na iGaming, který se dlouhodobě pohybuje v odvětví online hazardu. Má bohatou praxi v psaní článků, které propojují svět hráčů i profesionálů. Jeho specialitou je vytváření hlubokých analýz, přehledných recenzí a trendově orientovaného obsahu, který čtenářům přináší nejen zábavu, ale i znalosti.
                </p>
                <p className="text-gray-500 text-sm leading-relaxed">
                  Své texty staví na detailním výzkumu a praktické zkušenosti, díky čemuž pomáhá hráčům lépe se orientovat v prostředí online casin.
                </p>
              </div>
            </div>
            <div className="mt-5 pt-5 border-t border-[#eef1f7]">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="w-14 h-14 rounded-full overflow-hidden ring-2 ring-[#dde4ee]">
                    <img src={rebecaPhoto} alt="Rebeca Jimenez" className="w-full h-full object-cover" />
                  </div>
                </div>
                <div className="flex-1">
                  <div className="font-bold text-gray-900 text-base">Rebeca Jimenez</div>
                  <div className="text-gray-500 text-xs font-semibold mb-3">Fact Checker</div>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Rebeca působí jako Senior Editorial Manager a má za sebou více než 8 let práce v největších online magazínech věnovaných hazardu a iGaming komunitě. Na hrajbonus.com pracuje jako fact checker, který zajišťuje, aby všechny informace odpovídaly realitě, byly aktuální a reflektovaly poslední regulace i trendy na trhu.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Disclaimer */}
        <div className="flex items-center justify-center gap-2 text-xs text-gray-400 font-medium pb-4">
          <Shield className="w-3.5 h-3.5" />
          <span>Pouze pro hráče 18+. Hrajte zodpovědně. Podmínky se vztahují.</span>
        </div>

      </div>
      <Footer />
    </div>
  );
}
